/* Who is about to walk in: every booking starting inside the next few minutes,
 * nearest deadline first. Clicking one opens the assign sheet already filled in
 * — the type from the service mapping, the technician from the pick below — so
 * a client at the desk is two clicks from a square.
 *
 * Each chip also says who is going to pick that client up, which is a question
 * the book cannot answer on a busy afternoon: the name on a booking is a plan
 * made a fortnight ago, and the floor is what it is now. So it names whoever the
 * client asked for, or the booked technician while they can still take it, or
 * the rotation's next — see js/rules/taker.js for the order and store.js's
 * `dueTakerFor` for the measuring. The sheet opens on the same answer, because
 * a sheet that opens on somebody else has contradicted the row that opened it.
 *
 * Lifted out of service-bar.js when the phone grew its own copy. It is the same
 * list in both places and it must stay the same list: the desk's bar reads it
 * beside Next availability, and a phone reads it out of a collapsed strip, but
 * "who is due" is one question with one answer and a second implementation of
 * these chips is a second answer waiting to drift.
 *
 * The drag comes with it. On the bar a chip is also the turn — drag it onto a
 * column to start it there, onto a square to attach the client to what is
 * already running. Nothing on a phone does that, and nothing has to: the press
 * threshold means a tap that never moves is still a tap.
 */
import * as Vue from 'vue';
import { store } from '../store.js';
import { apptDrag } from './appt-drag.js';

export const DueNow = {
  name: 'DueNow',
  setup: function () {
    var drag = apptDrag;

    /* Who each chip is going to. One pass over the list rather than a call per
     * chip per frame — the same shape the service bar's Next availability map
     * has, and for the same reason: the answer is a walk over the whole floor
     * and the board redraws every minute.
     *
     * Keyed by the booked service, which is what a chip is. A visit with a
     * manicure and a pedicure in it is two chips and may well be two different
     * technicians, so the appointment is the wrong key. */
    var takers = Vue.computed(function () {
      var out = {};
      store.dueSoon.value.forEach(function (o) {
        out[o.svc.id] = store.dueTakerFor(o.appt, o.svc);
      });
      return out;
    });

    function takerOf(o) { return takers.value[o.svc.id] || null; }

    /* The chip's tooltip, which is where the reasoning goes.
     *
     * The name alone is the answer, and on a busy floor the answer is sometimes
     * surprising — the book says Ann and the chip says Bea. Somebody who wants
     * to know why gets a sentence rather than having to work it out from the
     * columns, and the sentence names the book's own technician either way so
     * nothing about the booking is hidden by the pick. */
    function takerNote(o) {
      var t = takerOf(o);
      var book = o.tech ? 'Booked with ' + o.tech : 'No technician on the booking';
      if (!t) return book + ' — nobody is free for this yet';
      if (t.kind === 'requested') {
        return t.name + ' was asked for' +
          (t.ready ? ' and is free' : ' and is not free yet — this one waits for them');
      }
      if (t.kind === 'booked' && t.ready) return book + ', and ' + t.name + ' is free';
      if (t.kind === 'booked') return book + ', who is not free yet';
      return book + ' — ' + t.name + ' is next up for this';
    }

    // Straight to the assign sheet rather than booking on the spot: an
    // appointment still needs a technician chosen, and that sheet already
    // pre-picks one, validates the type and offers attaching to a running turn.
    function pickAppt(o) {
      dropPeek();
      if (drag.suppressed()) return;               // that click ended a drag
      if (!store.presentStaff.value.length) {
        store.toast('Nobody is on the board yet.', 'warn');
        return;
      }
      store.openAssign(o.appt, o.svc);
    }

    function onChipDown(evt, o) {
      dropPeek();
      drag.start(evt, {
        serviceId: o.mapped ? o.mapped.id : null,
        staffIds: store.allowedStaffForBooked(o.svc),
        startsAt: o.svc.startsAt,
        durationMinutes: o.svc.durationMinutes,
        label: o.appt.clientName + ' · ' + o.svc.name,
        link: {
          appointmentId: o.appt.id, appointmentServiceId: o.svc.id,
          sourceServiceId: o.svc.serviceId, clientId: o.appt.clientId,
          clientName: o.appt.clientName, sourceServiceName: o.svc.name
        }
      });
    }

    // Same right-click as a line in today's book: they are the same booking
    // drawn twice, so they answer the same question the same way.
    function onChipContext(evt, o) {
      if (!store.deskMarksRequests.value) return;
      evt.preventDefault();
      store.openApptMenu(o.appt, o.svc, evt.clientX, evt.clientY);
    }

    function apptTime(o) {
      return store.formatClock(o.svc.startsAt);
    }

    /* ---- the hover card ----
     *
     * The same card Next availability opens, in the same box, because it is the
     * same question asked of a booking rather than of a walk-in: who takes it,
     * and if not them, who after. The chip's own arrow is the first row; the
     * two after it are whoever could start it soonest — see `dueNextList`.
     *
     * Mouse only. On a phone this list sits in a strip and a tap is a tap: a
     * card left hanging over the board by a pointerenter with no leave is worse
     * than no card. It goes the moment a press starts, too, since a press on
     * one of these may be the start of a drag.
     */
    var peek = Vue.reactive({ id: null, x: 0, y: 0, maxH: 0 });
    var peekTimer = null;
    var PEEK_W = 264;

    var peekItem = Vue.computed(function () {
      if (!peek.id) return null;
      return store.dueSoon.value.find(function (o) { return o.svc.id === peek.id; }) || null;
    });

    var peekList = Vue.computed(function () {
      var o = peekItem.value;
      return o ? store.dueNextList(o.appt, o.svc) : [];
    });

    function openPeek(evt, o) {
      if (evt.pointerType && evt.pointerType !== 'mouse') return;
      window.clearTimeout(peekTimer);
      var r = evt.currentTarget.getBoundingClientRect();
      var half = PEEK_W / 2;
      peek.x = Math.min(Math.max(r.left + r.width / 2, half + 8),
        window.innerWidth - half - 8);
      peek.y = r.bottom + 6;
      peek.maxH = Math.max(120, window.innerHeight - peek.y - 12);
      peek.id = o.svc.id;
    }

    function holdPeek() { window.clearTimeout(peekTimer); }

    function closePeek() {
      window.clearTimeout(peekTimer);
      peekTimer = window.setTimeout(function () { peek.id = null; }, 140);
    }

    function dropPeek() {
      window.clearTimeout(peekTimer);
      peek.id = null;
    }

    Vue.onBeforeUnmount(function () { window.clearTimeout(peekTimer); });

    return {
      dueSoon: store.dueSoon,
      takerOf: takerOf,
      takerNote: takerNote,
      pickAppt: pickAppt,
      onChipDown: onChipDown,
      onChipContext: onChipContext,
      apptTime: apptTime,
      peek: peek,
      peekItem: peekItem,
      peekList: peekList,
      openPeek: openPeek,
      holdPeek: holdPeek,
      closePeek: closePeek,
      isApptRequested: store.isApptRequested,
      noColor: store.defaultServiceColor
    };
  },
  template: [
    '<div class="quick-appts">',
    '  <span class="quick-appts__label">Due now',
    '    <span class="quick-appts__n">{{ dueSoon.length }}</span></span>',
    '  <div class="quick-appts__row">',
    '    <button v-for="o in dueSoon" :key="o.svc.id" type="button" class="appt-chip"',
    '      :class="{ \'is-late\': o.late }"',
    '      :style="{ \'--chip\': o.mapped ? o.mapped.color : noColor }"',
    '      :aria-label="o.appt.clientName + \', \' + o.svc.name + \'. \' + takerNote(o)"',
    '      @pointerenter="openPeek($event, o)" @pointerleave="closePeek"',
    '      @pointerdown="onChipDown($event, o)" @click="pickAppt(o)"',
    '      @contextmenu="onChipContext($event, o)">',
    '      <span class="appt-chip__time">{{ o.late ? "Due" : apptTime(o) }}</span>',
    '      <span class="appt-chip__main">',
    '        <span class="appt-chip__client">{{ o.appt.clientName }}<client-note :client-id="o.appt.clientId" />',
    '          <span v-if="isApptRequested(o.svc)" class="appt-chip__star"',
    '            title="Client request">&#9733;</span></span>',
    '        <span class="appt-chip__svc">{{ o.svc.name }}</span>',
    '      </span>',
    /* Who picks them up. The arrow rather than a "Next" label: there is no room
       for a word on a chip this size, and the row is already a sentence — this
       client, this service, to this person.

       Three ways for it to read. Plainly when somebody can take it now; quietly
       when the name is real but the minute is not — a request waiting on the
       person who was asked for, or the last name left on a full floor — and as
       the flat word it is when nobody can take it at all. Requests keep the
       board's gold, the same colour as the star beside the client's name, so a
       promise looks like a promise wherever it is drawn. */
    '      <span class="appt-chip__next"',
    '        :class="{ \'is-req\': takerOf(o) && takerOf(o).kind === \'requested\',',
    '                  \'is-wait\': takerOf(o) && !takerOf(o).ready,',
    '                  \'is-none\': !takerOf(o) }">',
    '        <span class="appt-chip__next-to" aria-hidden="true">&rarr;</span>',
    '        <span class="appt-chip__next-name">{{ takerOf(o) ? takerOf(o).name : "nobody free" }}</span>',
    '        <note-warn v-if="takerOf(o)" :client-id="o.appt.clientId" :staff-id="takerOf(o).staffId" />',
    '      </span>',
    '      <span class="appt-chip__letter">{{ o.mapped ? o.mapped.abbrev : "?" }}</span>',
    '    </button>',
    '  </div>',
    /* Fixed to the window, the same as Next availability's card beside it. */
    '  <div v-if="peekItem" class="peek-card svc-peek"',
    '    :style="{ left: peek.x + \'px\', top: peek.y + \'px\', maxHeight: peek.maxH + \'px\' }"',
    '    @pointerenter="holdPeek" @pointerleave="closePeek">',
    '    <div class="peek-card__head">{{ peekItem.late ? "Due" : apptTime(peekItem) }} &middot; {{ peekItem.appt.clientName }}<client-note :client-id="peekItem.appt.clientId" /></div>',
    '    <div class="svc-peek__svc">',
    '      <span class="svc-peek__letter" :style="{ \'--chip\': peekItem.mapped ? peekItem.mapped.color : noColor }">',
    '        {{ peekItem.mapped ? peekItem.mapped.abbrev : "?" }}</span>',
    '      <span class="svc-peek__name">{{ peekItem.svc.name }}</span>',
    '    </div>',
    '    <div class="svc-peek__meta">{{ peekItem.tech ? "Booked with " + peekItem.tech : "No technician on the booking" }}</div>',
    '    <ol v-if="peekList.length" class="svc-peek__list">',
    '      <li v-for="(e, i) in peekList" :key="e.staff.id" class="svc-peek__next"',
    '        :class="{ \'is-then\': i > 0 }">',
    '        <span class="svc-peek__next-label">{{ i === 0 ? "Next" : "Then" }}</span>',
    '        <span class="svc-peek__next-name" :class="{ \'is-wait\': e.wait !== \'now\' }">',
    '          {{ e.staff.name }}<note-warn :client-id="peekItem.appt.clientId" :staff-id="e.staff.id" /></span>',
    '        <span class="svc-peek__next-eta">{{ e.wait || "—" }}</span>',
    '      </li>',
    '    </ol>',
    '    <div v-else class="svc-peek__next">',
    '      <span class="svc-peek__next-label">Next</span>',
    '      <span class="svc-peek__next-name is-none">Nobody free for this</span>',
    '    </div>',
    '    <div class="peek-card__note">{{ takerNote(peekItem) }}. Click to assign, or drag onto a column.</div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
